// Oike — quoting a person, from Contactos and from Personas (docs/servicios/cotizaciones.md, 5.2).
// A quote is a Diagnostic Intake record. One made from a contact:
// - belongs to that contact by id (Stakeholder), never by name nor by being of the company;
// - says explicitly who is quoted (Quote Recipient: person or company). Having a company
//   does not mean contracting in its name: the company is linked only if Ale chooses it;
// - needs no form: it has no Token nor Status, so it is not a request (Solicitudes) and
//   it never duplicates one. The answers of a request of that contact, when there is one,
//   are only read (never copied) for the internal analysis;
// - is written through the usual commercial path (airtable-proxy), where the server checks
//   reach and the quote rules (src/quoteRules.js).
// Pure: the screens and scripts/test-person-quotes.mjs use the same functions.

export const RECIPIENTS = ['person', 'company'];
export const RECIPIENT_LABEL = { person: 'La persona', company: 'La empresa' };
const REC_RE = /^rec[A-Za-z0-9]{14}$/;

const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const text = (v, max = 200) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);

/** The contact's display name (Stakeholders: Name + Last name). */
export const contactNameOf = (c) => [text(c?.fields?.['Name']), text(c?.fields?.['Last name'])].filter(Boolean).join(' ').trim();

/** Who is quoted. Explicit only when Quote Recipient says it; older records are only described, never written. */
export function recipientOf(f = {}) {
  const v = name(f['Quote Recipient']);
  if (RECIPIENTS.includes(v)) return { value: v, explicit: true };
  return { value: links(f['Account']).length ? 'company' : 'person', explicit: false };
}

/**
 * Who a quote is for, without guessing (the screens group by it; nothing is written):
 * - Quote Recipient = person with her contact (Stakeholder) → 'personal'; without a contact → 'review';
 * - Quote Recipient = company with its company (Account) → 'company'; without one → 'review';
 * - no Quote Recipient (older records): 'company' only with its company AND proof that it came from
 *   the company flow: the company's form link (Token), a follow-up quote from a Diagnostic (Source
 *   Diagnostic) or a stored team request (Request Type = team). Anything else → 'review'. Having a
 *   company linked is not enough: it could be a personal or ambiguous contract.
 * 'review' and 'personal' are the administration's only (src/commercialScope.js › adminOnlyRecord).
 * `reasons` say which fields decided it, for the «Por revisar» list.
 */
export function classifyRecipient(f = {}) {
  const v = name(f['Quote Recipient']);
  const hasAccount = links(f['Account']).length > 0;
  const hasContact = links(f['Stakeholder']).length > 0;
  if (v === 'person') return hasContact ? { kind: 'personal', explicit: true, reasons: [] } : { kind: 'review', explicit: true, reasons: ['Para: persona, pero sin contacto vinculado'] };
  if (v === 'company') return hasAccount ? { kind: 'company', explicit: true, reasons: [] } : { kind: 'review', explicit: true, reasons: ['Para: empresa, pero sin empresa vinculada'] };
  const fromCompanyFlow = !!f['Token'] || links(f['Source Diagnostic']).length > 0 || name(f['Request Type']) === 'team';
  if (hasAccount && fromCompanyFlow) return { kind: 'company', explicit: false, reasons: ['Destinatario sin indicar; creada desde la empresa'] };
  return {
    kind: 'review', explicit: false,
    reasons: ['Destinatario sin indicar', hasAccount ? 'Empresa vinculada, pero sin prueba de que la contrata la empresa' : 'Sin empresa vinculada', hasContact ? 'Con un contacto vinculado' : 'Sin contacto vinculado'],
  };
}

/** A quote made from a contact (not a form): it carries Quote Recipient and is not a request. */
export const isContactQuote = (f = {}) => RECIPIENTS.includes(name(f['Quote Recipient'])) && !f['Token'] && f['Status'] !== 'Submitted';

/** A record that is a quote (not only a form request). */
export const hasQuote = (f = {}) => !!(name(f['Quote Status']) || f['Quote Lines JSON'] || links(f['Consulting Landing']).length || RECIPIENTS.includes(name(f['Quote Recipient'])));

/** The quotes of one contact, by id only (Diagnostic Intake › Stakeholder), newest first. */
export function quotesOfContact(intakes = [], stakeholderId) {
  if (!REC_RE.test(String(stakeholderId || ''))) return [];
  return intakes
    .filter(r => links(r.fields?.['Stakeholder']).includes(stakeholderId) && hasQuote(r.fields || {}))
    .sort((a, b) => String(b.createdTime || '').localeCompare(String(a.createdTime || '')));
}

/** The latest request (form submitted) of that contact: its answers are read for the analysis, never copied. */
export function latestRequestOf(intakes = [], stakeholderId) {
  if (!REC_RE.test(String(stakeholderId || ''))) return null;
  return intakes
    .filter(r => links(r.fields?.['Stakeholder']).includes(stakeholderId) && r.fields?.['Status'] === 'Submitted')
    .sort((a, b) => String(b.fields?.['Submitted At'] || b.createdTime || '').localeCompare(String(a.fields?.['Submitted At'] || a.createdTime || '')))[0] || null;
}

/**
 * The fields of a new quote for a contact. `recipient` must be chosen (no default).
 * - person: the company is linked only when `linkAccount` is true (a reference, not the payer);
 * - company: the contact's company is required and linked.
 */
export function newContactQuoteFields({ stakeholderId, contactName, recipient, account = null, linkAccount = false }) {
  if (!REC_RE.test(String(stakeholderId || ''))) throw new Error('La cotización necesita un contacto.');
  if (!RECIPIENTS.includes(recipient)) throw new Error('Elegí a quién se cotiza: la persona o la empresa.');
  const accountId = account?.id && REC_RE.test(account.id) ? account.id : '';
  if (recipient === 'company' && !accountId) throw new Error('Para cotizar a la empresa, el contacto necesita una empresa vinculada.');
  const who = text(contactName) || 'Contacto';
  const accountName = text(account?.name);
  const fields = {
    'Name': recipient === 'company' ? `${accountName || 'Empresa'} — cotización (contacto: ${who})` : `${who} — cotización individual`,
    'Stakeholder': [stakeholderId],
    'Quote Recipient': recipient,
    'Quote Status': 'Draft',
  };
  if (accountId && (recipient === 'company' || linkAccount)) fields['Account'] = [accountId];
  return fields;
}

/**
 * An open draft of this contact for the same recipient and company: opened instead of creating
 * another (double click, retry, two tabs).
 */
export function reusableDraftOf(quotes = [], { recipient, accountId = '' }) {
  return quotes.find(q => {
    const f = q.fields || {};
    return isContactQuote(f) && name(f['Quote Recipient']) === recipient && ['', 'Draft'].includes(name(f['Quote Status']))
      && !links(f['Consulting Landing']).length && (links(f['Account'])[0] || '') === (accountId || '');
  }) || null;
}

/**
 * A new quote to a company from «Cotizaciones y pagos»: its company is required; a contact of it is
 * optional (by id). No form: no Token nor Status, so it is not a request.
 */
export function newCompanyQuoteFields({ account, contact = null }) {
  if (!REC_RE.test(String(account?.id || ''))) throw new Error('Elegí la empresa.');
  const fields = {
    'Name': `${text(account.name) || 'Empresa'} — cotización${contact?.name ? ` (contacto: ${text(contact.name)})` : ''}`,
    'Account': [account.id],
    'Quote Recipient': 'company',
    'Quote Status': 'Draft',
  };
  if (contact?.id) {
    if (!REC_RE.test(contact.id)) throw new Error('Contacto no válido.');
    fields['Stakeholder'] = [contact.id];
  }
  return fields;
}

/** An equal open draft anywhere (same recipient, company and contact): reopened instead of creating another. */
export function openDraftLike(rows = [], fields = {}) {
  const want = { r: name(fields['Quote Recipient']), a: links(fields['Account'])[0] || '', s: links(fields['Stakeholder'])[0] || '' };
  return rows.find(q => {
    const f = q.fields || {};
    return isContactQuote(f) && name(f['Quote Recipient']) === want.r && ['', 'Draft'].includes(name(f['Quote Status']))
      && !links(f['Consulting Landing']).length && (links(f['Account'])[0] || '') === want.a && (links(f['Stakeholder'])[0] || '') === want.s;
  }) || null;
}
